import type { Middleware } from '@reduxjs/toolkit';
import { clearAuthStorage, isTokenExpired } from '@utils/tokenUtils';
import { REHYDRATE } from 'redux-persist';

import {
  setAuthToken,
  clearAuthToken,
  resetUser,
  updateUserId,
} from '../user/slices/userSlice';

/**
 * Middleware to sync auth token and userId with sessionStorage
 * - Saves token + userId to sessionStorage when setAuthToken/updateUserId is dispatched
 * - Removes token + userId from sessionStorage when clearAuthToken/resetUser is dispatched
 * - Rehydrates token from sessionStorage DURING redux-persist rehydration
 */
export const authTokenMiddleware: Middleware = store => next => action => {
  const isAction = (
    act: unknown,
  ): act is { type: string; payload?: unknown } => {
    return (
      typeof act === 'object' &&
      act !== null &&
      'type' in act &&
      typeof (act as { type: unknown }).type === 'string'
    );
  };

  if (isAction(action) && action.type === REHYDRATE) {
    const result = next(action);

    try {
      const token = sessionStorage.getItem('authToken');
      const expiresAt = sessionStorage.getItem('tokenExpiresAt');
      const userId = sessionStorage.getItem('userId');

      if (token && expiresAt) {
        // Check token expiration using centralized helper
        if (!isTokenExpired(expiresAt)) {
          // Token is still valid, restore it to Redux
          store.dispatch(setAuthToken({ token, expiresAt }));

          if (userId) {
            store.dispatch(updateUserId(userId));
          }
        } else {
          // Token is expired, clear from sessionStorage
          clearAuthStorage();
        }
      }
    } catch {
      // Silent fail
    }

    return result;
  }

  const result = next(action);

  // Save token to sessionStorage when setAuthToken is dispatched
  if (setAuthToken.match(action)) {
    try {
      sessionStorage.setItem('authToken', action.payload.token);
      sessionStorage.setItem('tokenExpiresAt', action.payload.expiresAt);
    } catch (error) {
      console.error('[authTokenMiddleware]  Error saving token:', error);
    }
    return result;
  }

  // Save userId to sessionStorage when updateUserId is dispatched
  if (updateUserId.match(action)) {
    try {
      sessionStorage.setItem('userId', action.payload);
    } catch (error) {
      console.error('[authTokenMiddleware] Error saving userId:', error);
    }
    return result;
  }

  // Delegate to updateUserId and setAuthToken actions
  if (isAction(action) && action.type === 'user/getUserById/fulfilled') {
    try {
      const payload = action.payload as
        | { data?: { id?: string; token?: string; expires_at?: string } }
        | undefined;
      const userId = payload?.data?.id;
      const token = payload?.data?.token;
      const expiresAt = payload?.data?.expires_at;

      // Delegate userId persistence to updateUserId action
      if (userId) {
        store.dispatch(updateUserId(userId));
      }

      // Delegate token persistence to setAuthToken action
      if (token && expiresAt) {
        store.dispatch(setAuthToken({ token, expiresAt }));
      }
    } catch {
      // Silent fail
    }
    return result;
  }

  // Clear token from sessionStorage when clearAuthToken is dispatched
  if (clearAuthToken.match(action)) {
    try {
      clearAuthStorage();
    } catch (error) {
      console.error('[authTokenMiddleware] Error clearing token:', error);
    }
    return result;
  }

  // Clear token from sessionStorage when resetUser is dispatched
  if (resetUser.match(action)) {
    try {
      clearAuthStorage();
    } catch (error) {
      console.error('[authTokenMiddleware] Error resetting user:', error);
    }
    return result;
  }

  return result;
};

export default authTokenMiddleware;
